import { ArrowForward } from "@mui/icons-material";
import { Box } from "@mui/material";
import { debounce } from "@mui/material/utils";
import { DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { deDE } from "@mui/x-date-pickers/locales";
import dayjs from "dayjs";
import { useCallback, useEffect, useRef } from "react";

export default function NewRangePicker({
  value,
  onChange,
  small = false,
  maximumDate,
  minimumDate,
  arrowIcon = <ArrowForward />,
  labels,
}: {
  value: [dayjs.Dayjs | null, dayjs.Dayjs | null];
  onChange: (newRange: [dayjs.Dayjs | null, dayjs.Dayjs | null]) => void;
  small?: boolean;
  maximumDate?: dayjs.Dayjs;
  minimumDate?: dayjs.Dayjs;
  arrowIcon?: React.ReactNode;
  labels?: [string, string];
}) {
  // keep latest onChange in a ref so our debounced closures always call the right one
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // create debounced validators exactly once
  const debouncedValidateStartRef = useRef(
    debounce((start: dayjs.Dayjs | null, end: dayjs.Dayjs | null) => {
      console.log("[validateStart]", { start, end });
      if (start && end && start.isAfter(end)) {
        console.log("[validateStart] adjusting end → start");
        onChangeRef.current([start, start]);
      }
    }, 500)
  );
  const debouncedValidateEndRef = useRef(
    debounce((start: dayjs.Dayjs | null, end: dayjs.Dayjs | null) => {
      console.log("[validateEnd]", { start, end });
      if (start && end && end.isBefore(start)) {
        console.log("[validateEnd] adjusting start → end");
        onChangeRef.current([end, end]);
      }
    }, 500)
  );

  // cleanup on unmount
  useEffect(() => {
    return () => {
      debouncedValidateStartRef.current.clear();
      debouncedValidateEndRef.current.clear();
    };
  }, []);

  // immediate change handlers + trigger debounced validation
  const handleStartChange = useCallback(
    (newStart: dayjs.Dayjs | null) => {
      console.log("[onChange start]", newStart);
      const currentEnd = value[1];
      onChange([newStart, currentEnd]);
      debouncedValidateStartRef.current(newStart, currentEnd);
    },
    [onChange, value[1]]
  );

  const handleEndChange = useCallback(
    (newEnd: dayjs.Dayjs | null) => {
      console.log("[onChange end]", newEnd);
      const currentStart = value[0];
      onChange([currentStart, newEnd]);
      debouncedValidateEndRef.current(currentStart, newEnd);
    },
    [onChange, value[0]]
  );

  const pickerProps = (isStart: boolean) => ({
    label: labels?.[isStart ? 0 : 1],
    slotProps: { textField: { size: small ? "small" as const : "medium" as const } },
    minDate: minimumDate,
    maxDate: maximumDate,
    value: isStart ? value[0] : value[1],
    onChange: isStart ? handleStartChange : handleEndChange,
    displayWeekNumber: true,
  });

  return (
    <LocalizationProvider
      adapterLocale="de"
      dateAdapter={AdapterDayjs}
      localeText={deDE.components.MuiLocalizationProvider.defaultProps.localeText}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <DatePicker enableAccessibleFieldDOMStructure={false} {...pickerProps(true)} />
        {arrowIcon}
        <DatePicker enableAccessibleFieldDOMStructure={false} {...pickerProps(false)} />
      </Box>
    </LocalizationProvider>
  );
}
